import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, X } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";
import { CATEGORIES } from "@/constants/brand";

const blank = { name: "", category: "water", description: "", flavors: [], price: 0, image_url: "", is_featured: false, is_best_seller: false, is_active: true, tags: [] };

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/products").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing({ ...blank }); setOpen(true); };
  const startEdit = (p) => { setEditing({ ...p, flavors: p.flavors || [], tags: p.tags || [] }); setOpen(true); };

  const save = async () => {
    const body = { ...editing, price: Number(editing.price) || 0 };
    delete body.id; delete body.created_at;
    try {
      if (editing.id) await api.put(`/admin/products/${editing.id}`, body);
      else await api.post(`/admin/products`, body);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-products" className="space-y-8">
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Products</div>
          <h1 className="editorial-h1 text-5xl mt-2">{items.length} on the shelf.</h1>
        </div>
        <button data-testid="admin-new-product" onClick={startNew} className="btn-pill btn-dark text-sm"><Plus size={16} /> New Product</button>
      </header>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((p) => (
          <div key={p.id} className="card-elevated overflow-hidden" data-testid={`adm-prod-${p.id}`}>
            {p.image_url && <img src={p.image_url} alt={p.name} className="aspect-[4/3] object-cover w-full" />}
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-red">{CATEGORIES.find((c)=>c.key===p.category)?.label || p.category}</div>
              <div className="font-display text-xl mt-1">{p.name}</div>
              <div className="text-xs text-brand-charcoal/65 mt-2 line-clamp-2">{p.description}</div>
              <div className="mt-4 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">{p.is_best_seller && <span className="tag bg-brand-red text-white">Best Seller</span>}{p.is_featured && <span className="tag">Featured</span>}{!p.is_active && <span className="text-brand-red">Inactive</span>}</div>
                <div className="font-medium">{p.price ? `₹${p.price}` : "—"}</div>
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={() => startEdit(p)} className="btn-pill btn-ghost text-xs border border-brand-line">Edit</button>
                <button onClick={() => del(p.id)} className="btn-pill btn-ghost text-xs"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {open && editing && (
        <div data-testid="prod-modal" className="fixed inset-0 z-50 bg-brand-charcoal/40 grid place-items-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-3xl">{editing.id ? "Edit product" : "New product"}</div>
              <button onClick={() => setOpen(false)} data-testid="prod-modal-close"><X /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Name</label><input data-testid="prod-f-name" className="pp-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Category</label>
                <select data-testid="prod-f-cat" className="pp-input bg-transparent" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                  {CATEGORIES.map((c)=>(<option key={c.key} value={c.key}>{c.label}</option>))}
                </select>
              </div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Price (₹)</label><input data-testid="prod-f-price" type="number" className="pp-input" value={editing.price || 0} onChange={(e) => setEditing({ ...editing, price: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Description</label><textarea data-testid="prod-f-desc" rows="3" className="pp-input" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Image URL</label><input data-testid="prod-f-img" className="pp-input" value={editing.image_url || ""} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Flavours (comma-separated)</label>
                <input data-testid="prod-f-flv" className="pp-input" value={(editing.flavors || []).join(", ")} onChange={(e) => setEditing({ ...editing, flavors: e.target.value.split(",").map((s)=>s.trim()).filter(Boolean) })} />
              </div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Tags</label>
                <input data-testid="prod-f-tags" className="pp-input" value={(editing.tags || []).join(", ")} onChange={(e) => setEditing({ ...editing, tags: e.target.value.split(",").map((s)=>s.trim()).filter(Boolean) })} />
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="prod-f-feat" checked={!!editing.is_featured} onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })} /> Featured</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="prod-f-best" checked={!!editing.is_best_seller} onChange={(e) => setEditing({ ...editing, is_best_seller: e.target.checked })} /> Best Seller</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="prod-f-active" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active</label>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setOpen(false)} className="btn-pill btn-ghost text-sm">Cancel</button>
              <button data-testid="prod-f-save" onClick={save} className="btn-pill btn-dark text-sm">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
